import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Briefcase,
  Calendar,
  DollarSign,
  FileText,
  Phone,
  BookmarkCheck,
  AlertCircle,
  ChevronDown,
  Search,
  Info,
  X,
} from "lucide-react";
import OfertaInfoModal from "../../components/UI/OfertaInfoModal/OfertaInfoModal.tsx";
import styles from "./OfertaDeTrabajo.module.scss";
import { connect } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import JurisdictionList from "../../components/UI/JurisdictionList/JurisdictionList";
import PlacesAutocomplete from "../../components/UI/PlacesAutocomplete/PlacesAutocomplete.tsx";
import { getSkills, getCities, createWorkOffer, openMainModalDashboard, closeMainModal } from "../../store/actions";
import { mapGoogleProvinceToJurisdiction, getLocalityFromAddressComponents } from "../../utils/jurisdictionFromPlace.ts";
import { JURISDICTIONS } from "../../constants/jurisdictions.ts";
import useGetTodayDate from "../../hooks/useGetTodayDate";
import { fireAnalytics } from "../../utils/firebase";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import environment from "../../environments";
import TurnstileWidget from "../../components/UI/TurnstileWidget/TurnstileWidget.tsx";
import type { TurnstileWidgetHandle } from "../../components/UI/TurnstileWidget/TurnstileWidget.tsx";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import { useActionGuard } from "../../hooks/useActionGuard.ts";
import { ReactComponent as WorkOfferEmptyIllustration } from "../../assets/svg/workOffer-empty-state.svg";

type ContactMethod = "email" | "phone";
type SalaryType = "Hour" | "Day" | "Week" | "Month" | "Job";

interface Skill {
  id: number;
  name: string;
}

interface City {
  name: string;
}

interface WorkZoneItem {
  jurisdiccion: number;
  name: string;
  city: string;
}

interface Jurisdiction {
  id: number;
  name: string;
}

interface UserData {
  id: number;
  username?: string;
  phone?: string;
}

interface WorkOfferFormValues {
  selectedJurisdiction: Jurisdiction | null;
  workZone: WorkZoneItem[];
  skills: Skill[];
  description: string;
  inmediateStart: boolean;
  startDate: string;
  contactMethod: ContactMethod;
  alternativeContact: string;
  phone: string | undefined;
  salary: number | "";
  salaryFormatted: string;
  activeUntil: string;
  salaryType: SalaryType;
  city: string;
}

interface OfertaDeTrabajoProps {
  isStandalone?: boolean;
  cities: City[];
  skills: Skill[];
  userData: UserData | null;
  getSkills: () => void;
  getCities: (jurisdictionId: number) => void;
  createWorkOffer: (payload: Record<string, unknown>) => void;
}

interface SelectedItemsListProps {
  items: WorkZoneItem[] | Skill[];
  onRemove: (id: number | string) => void;
  icon: typeof MapPin;
  emptyMessage: string;
}

const itemVariants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: -6,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 22,
      mass: 0.8,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -6,
    transition: {
      duration: 0.18,
      ease: "easeIn",
    },
  },
};

const emptyStateVariants = {
  initial: { opacity: 0, scale: 0.96 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.28,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.16,
      ease: "easeIn",
    },
  },
};

const containerVariants = {
  initial: { opacity: 0.8 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
      staggerChildren: 0.04,
    },
  },
  exit: {
    opacity: 0.8,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const SelectedItemsList = ({ items, onRemove, icon: Icon, emptyMessage }: SelectedItemsListProps) => (
  <motion.div
    className={styles.selectedItemsContainer}
    variants={containerVariants}
    initial="initial"
    animate="animate"
    layout
  >
    {items.length === 0 ? (
      <motion.div
        className={styles.selectedItemsEmpty}
        variants={emptyStateVariants}
        initial="initial"
        animate="animate"
        layout
      >
        <Icon size={14} />
        {emptyMessage}
      </motion.div>
    ) : (
      <motion.div className={styles.selectedItems} layout>
        {items.map((item, index) => {
          const entry = item as WorkZoneItem & Skill;
          const key = entry.id ?? `${entry.jurisdiccion}-${entry.city}-${index}`;
          return (
            <motion.button
              key={key}
              type="button"
              className={styles.selectedItem}
              variants={itemVariants}
              initial="initial"
              animate="animate"
              layout
              onClick={() => onRemove(entry.id ?? index)}
              whileTap={{ scale: 0.97, transition: { duration: 0.08 } }}
              aria-label="Quitar"
              title="Quitar"
            >
              <Icon size={12} className={styles.selectedItemIcon} />
              <span className={styles.selectedItemText}>{entry.city ? `${entry.name} · ${entry.city.split(",")[0]}` : entry.name}</span>
              <X size={14} className={styles.selectedItemRemove} aria-hidden />
            </motion.button>
          );
        })}
      </motion.div>
    )}
  </motion.div>
);

const OfertaDeTrabajo = ({
  isStandalone = false,
  cities,
  skills,
  userData,
  getSkills,
  getCities,
  createWorkOffer,
}: OfertaDeTrabajoProps) => {
  const [skillSearchValue, setSkillSearchValue] = useState<string>("");
  const [showSkillsList, setShowSkillsList] = useState<boolean>(false);
  const [salaryType, setSalaryType] = useState<SalaryType>("Hour");
  const [captchaReady, setCaptchaReady] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [hasEnteredFlow, setHasEnteredFlow] = useState(false);
  const [showOfertaInfo, setShowOfertaInfo] = useState(false);
  const [placeSearchValue, setPlaceSearchValue] = useState("");
  const [showManualLocationFallback, setShowManualLocationFallback] = useState(false);
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const { guard } = useActionGuard();
  const today = useGetTodayDate();
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const useCaptcha = Boolean(isStandalone && environment.turnstile?.siteKey);
  const captchaReadyForSubmit = !useCaptcha || captchaReady;

  const convertDateToISOFormat = useCallback((date: string) => {
    const [dd, mm, yyyy] = date.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const startDateInitialValue = useMemo(() => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  }, []);

  const initialValues: WorkOfferFormValues = useMemo(
    () => ({
      selectedJurisdiction: null,
      workZone: [],
      skills: [],
      description: "",
      inmediateStart: true,
      startDate: startDateInitialValue,
      contactMethod: "email",
      alternativeContact: userData?.username ?? "",
      phone: userData?.phone,
      salary: 0,
      salaryFormatted: "",
      activeUntil: "",
      salaryType,
      city: "",
    }),
    [startDateInitialValue, userData, salaryType]
  );

  const formSchema = Yup.object().shape({
    selectedJurisdiction: Yup.object().nullable(),
    workZone: Yup.array().min(1, "Debes seleccionar al menos una ciudad"),
    inmediateStart: Yup.boolean().required("Debes indicar si el inicio es inmediato"),
    activeUntil: Yup.string().required("Debes ingresar la fecha de finalización de la oferta"),
    skills: Yup.array().min(1, "*Debes seleccionar al menos un rubro de trabajo"),
  });

  const formik = useFormik<WorkOfferFormValues>({
    validateOnMount: true,
    validateOnBlur: true,
    initialValues,
    validationSchema: formSchema,
    onSubmit: async (values, helpers) => {
      if (useCaptcha) {
        const token = turnstileTokenRef.current;
        if (!token) return;
        await createWorkOffer({ ...values, captcha: token });
        helpers.resetForm();
        setCaptchaReady(false);
        turnstileTokenRef.current = null;
        turnstileRef.current?.reset();
        return;
      }

      await createWorkOffer({ ...values, captcha: undefined });
      helpers.resetForm();
    },
  });

  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    setFieldValue,
    handleChange,
    handleSubmit: submitForm,
  } = formik;

  const handleTurnstileVerify = useCallback((token: string) => {
    turnstileTokenRef.current = token;
    setCaptchaReady(true);
  }, []);

  const handleTurnstileError = useCallback(() => {
    turnstileTokenRef.current = null;
    setCaptchaReady(false);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    turnstileTokenRef.current = null;
    setCaptchaReady(false);
  }, []);

  const showCaptcha = useCaptcha && environment.turnstile.siteKey && isValid;

  useEffect(() => {
    if (!showCaptcha) setCaptchaReady(false);
  }, [showCaptcha]);

  useEffect(() => {
    getSkills();
    if (userData?.id) {
      
      // Nuevo evento estandarizado
      fireAnalytics("work_offer_modal_opened", {
        edify_user_id: userData.id,
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "work_offers",
        action: "modal_opened"
      });
    }
  }, [getSkills, userData]);

  useEffect(() => {
    if (values.selectedJurisdiction) {
      getCities(values.selectedJurisdiction.id);
    }
  }, [values.selectedJurisdiction, getCities]);

  useEffect(() => {
    if (values.inmediateStart) {
      setFieldValue("startDate", convertDateToISOFormat(today));
    }
  }, [values.inmediateStart, convertDateToISOFormat, setFieldValue, today]);

  const filteredSkills = useMemo(
    () =>
      skills.filter((skill) => skill.name.toLowerCase().includes(skillSearchValue.toLowerCase())),
    [skills, skillSearchValue]
  );

  const handleAddSkill = useCallback(
    (skill: Skill) => {
      if (!skill?.id) return;
      const exists = values.skills.some((current) => current.id === skill.id);
      if (!exists) {
        setFieldValue("skills", [...values.skills, { id: skill.id, name: skill.name }]);
      }
      setSkillSearchValue("");
      setShowSkillsList(false);
    },
    [values.skills, setSkillSearchValue, setShowSkillsList, setFieldValue]
  );

  const handleRemoveSkill = useCallback(
    (skillId: number | string) => {
      setFieldValue(
        "skills",
        values.skills.filter((skill) => skill.id !== skillId)
      );
    },
    [values.skills, setFieldValue]
  );

  const handleAddWorkZone = useCallback(
    (city: City) => {
      if (!values.selectedJurisdiction) return;
      const entry: WorkZoneItem = {
        jurisdiccion: values.selectedJurisdiction.id,
        name: values.selectedJurisdiction.name,
        city: city.name,
      };
      setFieldValue("workZone", [...values.workZone, entry]);
      setFieldValue("city", city.name);
      setFieldValue("selectedJurisdiction", null);
    },
    [values.workZone, values.selectedJurisdiction, setFieldValue]
  );

  const handleAddWorkZoneFromPlace = useCallback(
    (place: { address: string; addressComponents?: Array<{ long_name: string; short_name: string; types: string[] }> }) => {
      const components = place.addressComponents;
      const jurisdictionId = mapGoogleProvinceToJurisdiction(components, JURISDICTIONS);
      const locality = getLocalityFromAddressComponents(components);
      const cityName = locality?.trim() || place.address.split(",")[0]?.trim() || place.address;
      if (jurisdictionId != null) {
        const jurisdiction = JURISDICTIONS.find((j) => j.id === jurisdictionId);
        if (jurisdiction) {
          const entry: WorkZoneItem = {
            jurisdiccion: jurisdiction.id,
            name: jurisdiction.name,
            city: cityName,
          };
          const exists = values.workZone.some(
            (z) => z.jurisdiccion === entry.jurisdiccion && z.city.toLowerCase() === entry.city.toLowerCase()
          );
          if (!exists) {
            setFieldValue("workZone", [...values.workZone, entry]);
            getCities(jurisdiction.id);
          }
          setPlaceSearchValue("");
        }
      } else {
        setShowManualLocationFallback(true);
      }
    },
    [values.workZone, setFieldValue, getCities]
  );

  const handleRemoveWorkZone = useCallback(
    (index: number | string) => {
      setFieldValue(
        "workZone",
        values.workZone.filter((_, currentIndex) => currentIndex !== Number(index))
      );
    },
    [values.workZone, setFieldValue]
  );

  const handleSalaryTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value as SalaryType;
      setSalaryType(value);
      setFieldValue("salaryType", value);
    },
    [setFieldValue]
  );

  const handleSalaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\./g, "").replace(/\D/g, "");
      if (raw) {
        const numberValue = Number.parseInt(raw, 10);
        if (!Number.isNaN(numberValue)) {
          const formatted = new Intl.NumberFormat("es-ES").format(numberValue);
          setFieldValue("salary", numberValue);
          setFieldValue("salaryFormatted", formatted);
        }
      } else {
        setFieldValue("salary", "");
        setFieldValue("salaryFormatted", "");
      }
    },
    [setFieldValue]
  );

  const handleContactMethod = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value as ContactMethod;
      setFieldValue("contactMethod", value);
      if (value === "phone") {
        setFieldValue("alternativeContact", userData?.phone ?? "");
      } else {
        setFieldValue("alternativeContact", userData?.username ?? "");
      }
    },
    [setFieldValue, userData]
  );

  const isFieldComplete = useCallback(
    (fieldName: "workZone" | "skills" | "activeUntil") => {
      if (fieldName === "workZone") return values.workZone.length > 0;
      if (fieldName === "skills") return values.skills.length > 0;
      if (fieldName === "activeUntil") return values.activeUntil !== "";
      return false;
    },
    [values]
  );

  const handleSubmit = useCallback(() => {
    setHasAttemptedSubmit(true);
    submitForm();
  }, [submitForm]);

  const renderFormSections = () => (
    <>
      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <MapPin size={18} />
          <div>
            <h3 className={styles.sectionTitle}>
              Ubicación
              {!isFieldComplete("workZone") && <span className={styles.required}>*</span>}
            </h3>
            <span className={styles.sectionHint}>Define provincias y ciudades donde se realizará el trabajo</span>
          </div>
        </header>

        <div className={styles.placeSearchWrap}>
          <label className={styles.label}>Ubicación</label>
          <PlacesAutocomplete
            value={placeSearchValue}
            onChange={setPlaceSearchValue}
            onPlaceSelect={handleAddWorkZoneFromPlace}
            placeholder="Ciudad o dirección"
            className={styles.placeSearchInput}
            hideLabel
          />
        </div>

        <SelectedItemsList
          items={values.workZone}
          onRemove={handleRemoveWorkZone}
          icon={MapPin}
          emptyMessage="No hay ubicaciones seleccionadas"
        />

        {showManualLocationFallback && (
          <div className={styles.placeManualBlock}>
            <div className={styles.fieldGridTwo}>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Provincia
                  {!isFieldComplete("workZone") && <span className={styles.required}>*</span>}
                </label>
                <select
                  className={`${styles.select} ${
                    touched.workZone && errors.workZone ? styles.inputError : ""
                  }`}
                  value={values.selectedJurisdiction?.id ?? ""}
                  onChange={(event) => {
                    const selected = JurisdictionList.find(
                      (jurisdiction) => jurisdiction.id === Number.parseInt(event.target.value, 10)
                    );
                    setFieldValue("selectedJurisdiction", selected ?? null);
                  }}
                >
                  <option value="">Seleccionar provincia</option>
                  {JurisdictionList.map((jurisdiction: Jurisdiction) => (
                    <option key={jurisdiction.id} value={jurisdiction.id}>
                      {jurisdiction.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>
                  Ciudad
                  {!isFieldComplete("workZone") && <span className={styles.required}>*</span>}
                </label>
                <select
                  className={`${styles.select} ${
                    touched.workZone && errors.workZone ? styles.inputError : ""
                  }`}
                  disabled={!values.selectedJurisdiction}
                  value=""
                  onChange={(event) => {
                    const city = cities.find((entry) => entry.name === event.target.value);
                    if (city) handleAddWorkZone(city);
                  }}
                >
                  <option value="">Seleccionar ciudad</option>
                  {cities.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {touched.workZone && errors.workZone && (
                  <div className={styles.fieldError}>
                    <AlertCircle size={14} />
                    {errors.workZone as string}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <Briefcase size={18} />
          <div>
            <h3 className={styles.sectionTitle}>
              Rubro
              {!isFieldComplete("skills") && <span className={styles.required}>*</span>}
            </h3>
            <span className={styles.sectionHint}>Selecciona los rubros o habilidades requeridas</span>
          </div>
        </header>

        <SelectedItemsList
          items={values.skills}
          onRemove={handleRemoveSkill}
          icon={Briefcase}
          emptyMessage="No hay rubros seleccionados"
        />

        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Seleccionar rubro
            {!isFieldComplete("skills") && <span className={styles.required}>*</span>}
          </label>
          <div className={styles.skillSelector}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                placeholder="Buscar rubro..."
                value={skillSearchValue}
                onChange={(event) => {
                  const value = event.target.value;
                  setSkillSearchValue(value);
                  setShowSkillsList(value.length > 0);
                }}
                className={`${styles.input} ${
                  touched.skills && errors.skills ? styles.inputError : ""
                }`}
              />
              <Search size={16} className={styles.searchIcon} />
              <button
                type="button"
                className={`${styles.dropdownTrigger} ${showSkillsList ? styles.dropdownOpen : ""}`}
                onClick={() => setShowSkillsList(!showSkillsList)}
              >
                <ChevronDown size={16} />
              </button>
            </div>

            {showSkillsList && (
              <motion.div
                className={styles.dropdown}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {filteredSkills.length > 0 ? (
                  filteredSkills.map((skill) => (
                    <button
                      key={skill.id}
                      type="button"
                      className={styles.dropdownItem}
                      onClick={() => handleAddSkill(skill)}
                    >
                      {skill.name}
                    </button>
                  ))
                ) : (
                  <div className={styles.dropdownItemMuted}>
                    {skillSearchValue ? "No se encontraron rubros" : "Cargando rubros..."}
                  </div>
                )}
              </motion.div>
            )}
          </div>
          {touched.skills && errors.skills && (
            <div className={styles.fieldError}>
              <AlertCircle size={14} />
              {errors.skills as string}
            </div>
          )}
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <Calendar size={18} />
          <div>
            <h3 className={styles.sectionTitle}>
              Fechas
              {!isFieldComplete("activeUntil") && <span className={styles.required}>*</span>}
            </h3>
            <span className={styles.sectionHint}>Completá las dos fechas: cuándo empieza la vacante y hasta cuándo se puede postular en Edify</span>
          </div>
        </header>

        <div className={styles.dateGrid}>
          <div className={styles.dateColumn}>
            <h4 className={styles.dateTitle}>Inicio de la vacante</h4>
            <p className={styles.dateDescription}>Cuándo esperás que la persona empiece a trabajar.</p>
            <div className={styles.checkboxRow}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={values.inmediateStart}
                  onChange={handleChange}
                  name="inmediateStart"
                  className={styles.checkbox}
                />
                Inicio inmediato
              </label>
            </div>

            {!values.inmediateStart && (
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Fecha específica de inicio</label>
                <div className={styles.dateInput}>
                  <input
                    type="date"
                    className={styles.input}
                    min={convertDateToISOFormat(today)}
                    max={values.activeUntil}
                    value={values.startDate}
                    onChange={handleChange}
                    name="startDate"
                  />
                </div>
              </div>
            )}
          </div>

          <div className={styles.dateColumn}>
            <h4 className={styles.dateTitle}>Último día para postularse</h4>
            <p className={styles.dateDescription}>
              Seleccioná el último día en que querés recibir postulaciones en Edify. Ese día la publicación se cierra automáticamente.
            </p>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Último día visible
                {!isFieldComplete("activeUntil") && <span className={styles.required}>*</span>}
              </label>
              <div className={styles.dateInput}>
                <input
                  type="date"
                  className={`${styles.input} ${
                    touched.activeUntil && errors.activeUntil ? styles.inputError : ""
                  }`}
                  min={values.startDate || convertDateToISOFormat(today)}
                  value={values.activeUntil}
                  onChange={handleChange}
                  name="activeUntil"
                />
              </div>
              {touched.activeUntil && errors.activeUntil && (
                <div className={styles.fieldError}>
                  <AlertCircle size={14} />
                  {errors.activeUntil as string}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <DollarSign size={18} />
          <div>
            <h3 className={styles.sectionTitle}>Remuneración</h3>
            <span className={styles.sectionHint}>Define el monto y la modalidad de pago ofrecida</span>
          </div>
        </header>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>Monto ofrecido</label>
          <div className={styles.salaryRow}>
            <span className={styles.currencySymbol}>$</span>
            <input
              type="text"
              placeholder="Ingresar monto"
              className={styles.salaryInput}
              value={values.salaryFormatted}
              onChange={handleSalaryChange}
              name="salary"
            />
            <select
              value={values.salaryType}
              onChange={handleSalaryTypeChange}
              className={styles.salarySelect}
              name="salaryType"
            >
              <option value="Hour">por Hora</option>
              <option value="Day">por Día</option>
              <option value="Week">por Semana</option>
              <option value="Month">por Mes</option>
              <option value="Job">Trabajo completo</option>
            </select>
          </div>
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <FileText size={18} />
          <div>
            <h3 className={styles.sectionTitle}>Descripción</h3>
            <span className={styles.sectionHint}>
              Describe responsabilidades, requisitos y beneficios del puesto
            </span>
          </div>
        </header>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>Detalles de la oferta</label>
          <textarea
            placeholder="Describe los requisitos, responsabilidades, beneficios y cualquier información relevante sobre la posición..."
            className={styles.textarea}
            value={values.description}
            onChange={handleChange}
            name="description"
            rows={6}
          />
        </div>
      </section>

      <section className={styles.cardSection}>
        <header className={styles.sectionHeader}>
          <Phone size={18} />
          <div>
            <h3 className={styles.sectionTitle}>Información de contacto</h3>
            <span className={styles.sectionHint}>Selecciona cómo querés recibir las postulaciones</span>
          </div>
        </header>

        <div className={styles.fieldGridTwo}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Método de contacto</label>
            <select
              value={values.contactMethod}
              onChange={handleContactMethod}
              className={styles.select}
              name="contactMethod"
            >
              <option value="email">Email</option>
              <option value="phone">Teléfono</option>
            </select>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Contacto</label>
            <input
              type="text"
              placeholder="Ingresar contacto"
              value={values.alternativeContact}
              onChange={handleChange}
              className={styles.input}
              name="alternativeContact"
            />
          </div>
        </div>
      </section>
    </>
  );

  const formatSummaryDate = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const salaryTypeLabel: Record<SalaryType, string> = {
    Hour: "por hora",
    Day: "por día",
    Week: "por semana",
    Month: "por mes",
    Job: "trabajo completo",
  };

  const summarySections = [
    {
      key: "workZone",
      icon: MapPin,
      label: "Ubicación",
      done: values.workZone.length > 0,
      hasError: Boolean(errors.workZone),
      text: values.workZone.length > 0 ? `${values.workZone.length} ciudad${values.workZone.length !== 1 ? "es" : ""}` : "Falta agregar",
    },
    {
      key: "skills",
      icon: Briefcase,
      label: "Rubro",
      done: values.skills.length > 0,
      hasError: Boolean(errors.skills),
      text: values.skills.length > 0 ? `${values.skills.length} rubro${values.skills.length !== 1 ? "s" : ""}` : "Falta agregar",
    },
    {
      key: "dates",
      icon: Calendar,
      label: "Fechas",
      done: Boolean(values.activeUntil),
      hasError: Boolean(errors.activeUntil),
      text: values.activeUntil
        ? `Inicio ${values.inmediateStart ? "inmediato" : formatSummaryDate(values.startDate)}, visible hasta ${formatSummaryDate(values.activeUntil)}`
        : "Falta completar",
    },
    {
      key: "salary",
      icon: DollarSign,
      label: "Remuneración",
      done: Boolean(values.salary && values.salary > 0),
      hasError: false,
      text: values.salary && values.salary > 0 ? `$ ${values.salaryFormatted || values.salary} ${salaryTypeLabel[values.salaryType]}` : "Opcional",
    },
    {
      key: "description",
      icon: FileText,
      label: "Descripción",
      done: values.description.trim().length > 0,
      hasError: false,
      text: values.description.trim().length > 0 ? "Completada" : "Opcional",
    },
    {
      key: "contact",
      icon: Phone,
      label: "Contacto",
      done: Boolean(values.alternativeContact?.trim()),
      hasError: false,
      text: values.alternativeContact?.trim()
        ? `${values.contactMethod === "email" ? "Email" : "Teléfono"}: ${values.alternativeContact.trim()}`
        : "Opcional",
    },
  ];

  const summaryBlock = (
    <div className={styles.summaryBlock}>
      <div className={styles.summaryBlockHeader}>
        <h3 className={styles.summaryBlockTitle}>Tu vacante</h3>
        <button
          type="button"
          className={styles.summaryBlockInfoBtn}
          onClick={() => setShowOfertaInfo(true)}
          aria-label="Más información sobre los beneficios de publicar en Edify"
        >
          <Info size={16} />
        </button>
      </div>
      <p className={styles.summaryBlockIntro}>
        Estás creando una vacante para contratar. Cuando la publiques, los profesionales podrán postularse a tu búsqueda.
      </p>
      <ul className={styles.summaryBlockList}>
        {summarySections.map(({ key, icon: Icon, label, done, hasError, text }) => {
          const showError = hasAttemptedSubmit && hasError && !done;
          return (
            <li key={key} className={`${styles.summaryBlockItem} ${showError ? styles.summaryBlockItemError : ""}`}>
              <Icon size={14} className={done ? styles.summaryBlockIconDone : showError ? styles.summaryBlockIconError : styles.summaryBlockIconPending} />
              <div className={styles.summaryBlockItemContent}>
                <span className={styles.summaryBlockItemLabel}>{label}</span>
                <span className={done ? styles.summaryBlockItemText : showError ? styles.summaryBlockItemTextError : styles.summaryBlockItemTextPending}>{text}</span>
              </div>
            </li>
          );
        })}
      </ul>
      <div className={styles.summaryBlockSubmit}>
        {showCaptcha && (
          <div className={styles.turnstileWrapper}>
            <TurnstileWidget
              ref={turnstileRef}
              sitekey={environment.turnstile.siteKey}
              onVerify={handleTurnstileVerify}
              onExpire={handleTurnstileExpire}
              onError={handleTurnstileError}
              appearance="interaction-only"
              size="normal"
            />
          </div>
        )}
        <button className={styles.submitButton} onClick={guard("create_work_offer", handleSubmit)} disabled={!isValid || !captchaReadyForSubmit}>
          <BookmarkCheck size={18} />
          {isSubmitting
            ? (useCaptcha ? "Verificando..." : "Publicando...")
            : "Publicar vacante"}
        </button>
        {hasAttemptedSubmit && !isValid && (
          <div className={styles.formError}>
            <AlertCircle size={16} />
            Por favor, completá todos los campos obligatorios marcados con *
          </div>
        )}
      </div>
    </div>
  );

  const submitBlock = (
    <>
      {showCaptcha && (
        <div className={styles.turnstileWrapper}>
          <TurnstileWidget
            ref={turnstileRef}
            sitekey={environment.turnstile.siteKey}
            onVerify={handleTurnstileVerify}
            onExpire={handleTurnstileExpire}
            onError={handleTurnstileError}
            appearance="interaction-only"
            size="normal"
          />
        </div>
      )}
      <button className={styles.submitButton} onClick={guard("create_work_offer", handleSubmit)} disabled={!isValid || !captchaReadyForSubmit}>
        <BookmarkCheck size={18} />
        {isSubmitting
          ? (useCaptcha ? "Verificando..." : "Publicando...")
          : "Publicar vacante"}
      </button>
      {hasAttemptedSubmit && !isValid && (
        <div className={styles.formError}>
          <AlertCircle size={16} />
          Por favor, completá todos los campos obligatorios marcados con *
        </div>
      )}
    </>
  );

  const formContent = (
    <div className={styles.formShell}>
      {renderFormSections()}
      <div className={styles.submitArea}>
        {submitBlock}
      </div>
    </div>
  );

  if (isStandalone) {
    return (
      <div className={styles.page}>
        <div className={`${styles.wrapper} ${styles.standaloneWrapper}`}>
          <header className={styles.headerStandalone}>
            <h1 className={styles.titleStandalone}>Publicar vacante</h1>
            <p className={styles.subtitleStandalone}>Completá los datos de la vacante para que profesionales se postulen a tu búsqueda</p>
          </header>
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumbs />
          <p className={styles.subtitle}>Completá los datos de la vacante que querés publicar para que profesionales se postulen a tu búsqueda</p>
        </div>
        <main className={styles.main}>
          <div className={styles.bodyColumn}>
            {!hasEnteredFlow ? (
              <div className={styles.welcomeBlock}>
                <WorkOfferEmptyIllustration className={styles.welcomeIllustration} aria-hidden />
                <h2 className={styles.welcomeTitle}>Publicá una vacante</h2>
                <p className={styles.welcomeText}>
                  ¿Necesitás contratar? Acá publicás la vacante o búsqueda para que profesionales se postulen. Completá ubicación, rubros, fechas y cómo contactarte.
                </p>
                <button
                  type="button"
                  className={styles.welcomeButton}
                  onClick={() => {
                    fireAnalytics("work_offer_empty_state_cta_click", {
                      category: "work_offers",
                      action: "empty_state_begin",
                      session_id: getSessionId(),
                      device_category: getDeviceCategory(),
                      ...(userData?.id && { edify_user_id: userData.id }),
                    });
                    setHasEnteredFlow(true);
                  }}
                  disabled={isSubmitting}
                >
                  Comenzar
                </button>
              </div>
            ) : (
              <div className={styles.formShell}>
                {renderFormSections()}
              </div>
            )}
          </div>
          <aside className={styles.summaryColumn}>
            {hasEnteredFlow ? summaryBlock : (
              <div className={styles.welcomeSummary}>
                <p className={styles.welcomeSummaryText}>Hacé clic en <strong>Comenzar</strong> para cargar los datos de la vacante que querés publicar.</p>
              </div>
            )}
          </aside>
        </main>
      </div>
      <OfertaInfoModal isOpen={showOfertaInfo} onClose={() => setShowOfertaInfo(false)} />
    </div>
  );
};

const mapStateToProps = (state: Record<string, any>) => ({
  skills: state.register.skills,
  cities: state.register.cities,
  userData: state.general.userData,
});

export default connect(mapStateToProps, {
  getSkills,
  getCities,
  createWorkOffer,
  openMainModalDashboard,
  closeMainModal,
})(OfertaDeTrabajo);

